/**
 * Rascunho local da chamada em andamento (por turma + data), gravado a cada
 * toque no checklist. Sobrevive a recarregamento da página e a descarte da aba
 * pelo celular — até o "Salvar" ser confirmado pela API, as marcações só
 * existiam na memória. Best-effort: localStorage pode falhar (aba anônima,
 * cota), então toda leitura/escrita é protegida e a tela funciona sem ele.
 */
const PREFIX = 'sheepcount:chamada-rascunho:';
/** Rascunhos mais velhos que isso são descartados (chamada já não é relevante). */
const VALIDADE_MS = 14 * 24 * 60 * 60 * 1000;

export interface ChamadaDraft {
  turmaId: string;
  data: string; // YYYY-MM-DD
  checklist: Record<string, boolean>;
  observacoes: string;
  atualizadoEm: number;
}

function chave(turmaId: string, data: string): string {
  return `${PREFIX}${turmaId}:${data}`;
}

function ler(key: string): ChamadaDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    const draft = JSON.parse(raw) as ChamadaDraft;
    if (Date.now() - draft.atualizadoEm > VALIDADE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function lerRascunho(turmaId: string, data: string): ChamadaDraft | null {
  return ler(chave(turmaId, data));
}

export function salvarRascunho(draft: Omit<ChamadaDraft, 'atualizadoEm'>): void {
  try {
    localStorage.setItem(
      chave(draft.turmaId, draft.data),
      JSON.stringify({ ...draft, atualizadoEm: Date.now() }),
    );
  } catch {
    // sem armazenamento local: segue só em memória
  }
}

export function apagarRascunho(turmaId: string, data: string): void {
  try {
    localStorage.removeItem(chave(turmaId, data));
  } catch {
    // nada a fazer
  }
}

/** Rascunhos válidos ainda não salvos, de todas as turmas (para avisar na lista). */
export function listarRascunhos(): ChamadaDraft[] {
  const drafts: ChamadaDraft[] = [];
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) {
        const draft = ler(key);
        if (draft) drafts.push(draft);
      }
    }
  } catch {
    // sem armazenamento local
  }
  return drafts;
}
